import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Search,
  Building2,
  CreditCard,
  Gift,
  Link as LinkIcon,
  MoreVertical,
  User as UserIcon,
  Check,
  X,
  Loader2,
  Copy,
  Plus,
  Sparkles
} from 'lucide-react';
import { adminService } from '../../services/adminService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import AiCreditsModal from '../../components/admin/AiCreditsModal';
import { User, Plan, Subscription } from '../../types';

interface CompanyUser extends User {
  subscriptions?: (Subscription & { plan?: Plan })[];
}

const AdminCompaniesPage: React.FC = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<CompanyUser[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  const [isAssignPlanModalOpen, setIsAssignPlanModalOpen] = useState(false);
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState<string | null>(null);

  // Form states
  const [assignForm, setAssignForm] = useState({
    plan_id: '',
    billing_cycle: 'monthly',
    is_free: false,
    custom_price: '',
    expires_at: '',
    notes: '',
    custom_limits: {
      campaigns: '',
      projects: '',
      keywords: '',
      competitors: '',
      content_generations: '',
      team_members: '',
      automated_workflows: '',
      image_generations: ''
    } as Record<string, string>
  });

  const [paymentLinkForm, setPaymentLinkForm] = useState({
    plan_id: '',
    billing_cycle: 'monthly',
    currency: 'brl',
    custom_price: ''
  });

  const [generatedLink, setGeneratedLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Créditos de IA (mesma janela da página Admin > Créditos de IA)
  const [aiCreditsUser, setAiCreditsUser] = useState<{ id: string; label: string } | null>(null);

  // Video credits modal state
  const [isVideoCreditsModalOpen, setIsVideoCreditsModalOpen] = useState(false);
  const [videoCreditsLoading, setVideoCreditsLoading] = useState(false);
  const [videoCreditsBalance, setVideoCreditsBalance] = useState<{
    total: number; used: number; available: number; monthly_free: number;
  } | null>(null);
  const [videoCreditsTransactions, setVideoCreditsTransactions] = useState<Array<{
    id: string; type: 'debit' | 'credit' | 'refund' | 'grant'; amount: number; source: string;
    description?: string | null; balance_after?: number | null; created_at: string;
  }>>([]);
  const [videoCreditsForm, setVideoCreditsForm] = useState({ amount: '', notes: '' });

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCompanies();
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [page, planFilter, search]);

  // Update custom_price when plan, billing_cycle, or currency changes
  useEffect(() => {
    if (paymentLinkForm.plan_id) {
      const selectedPlan = plans.find(p => p.id === paymentLinkForm.plan_id);
      if (selectedPlan) {
        let price = 0;
        if (paymentLinkForm.currency === 'usd') {
          price = paymentLinkForm.billing_cycle === 'yearly'
            ? (selectedPlan as any).price_usd_yearly || 0
            : (selectedPlan as any).price_usd_monthly || 0;
        } else {
          price = paymentLinkForm.billing_cycle === 'yearly'
            ? selectedPlan.price_yearly
            : selectedPlan.price_monthly;
        }
        setPaymentLinkForm(prev => ({ ...prev, custom_price: price.toString() }));
      }
    }
  }, [paymentLinkForm.plan_id, paymentLinkForm.billing_cycle, paymentLinkForm.currency, plans]);

  const loadPlans = async () => {
    try {
      const response = await adminService.getPlans();
      if (response.success && response.data) {
        // Filter out custom plan from payment link generation
        const filteredPlans = response.data.filter((plan: Plan) => plan.slug !== 'custom');
        setPlans(filteredPlans);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const response = await adminService.getCompanies({
        page,
        limit: 15,
        search: search || undefined,
        plan_id: planFilter || undefined
      });
      if (response.success && response.data) {
        setCompanies(response.data as CompanyUser[]);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadCompanies();
  };

  const openAssignPlanModal = (user: CompanyUser) => {
    setSelectedUser(user);
    const currentSub = user.subscriptions?.[0];
    const existingCustomLimits = (currentSub?.metadata as any)?.custom_limits || {};
    const limitFields = ['campaigns','projects','keywords','competitors','content_generations','team_members','automated_workflows'];
    const custom_limits: Record<string, string> = {};
    limitFields.forEach(f => {
      custom_limits[f] = existingCustomLimits[f] !== undefined && existingCustomLimits[f] !== null ? String(existingCustomLimits[f]) : '';
    });
    setAssignForm({
      plan_id: currentSub?.plan_id || '',
      billing_cycle: currentSub?.billing_cycle || 'monthly',
      is_free: currentSub?.metadata?.is_free || false,
      custom_price: currentSub?.metadata?.custom_price?.toString() || '',
      expires_at: currentSub?.current_period_end ? new Date(currentSub.current_period_end).toISOString().split('T')[0] : '',
      notes: currentSub?.metadata?.admin_notes || '',
      custom_limits
    });
    setIsAssignPlanModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const openPaymentLinkModal = (user: CompanyUser) => {
    setSelectedUser(user);
    setPaymentLinkForm({
      plan_id: '',
      billing_cycle: 'monthly',
      currency: 'brl',
      custom_price: ''
    });
    setGeneratedLink('');
    setIsPaymentLinkModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleAssignPlan = async () => {
    if (!selectedUser || !assignForm.plan_id) return;

    setIsSaving(true);
    try {
      const customLimitsPayload: Record<string, number> = {};
      Object.entries(assignForm.custom_limits).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          const num = Number(v);
          if (!Number.isNaN(num)) customLimitsPayload[k] = num;
        }
      });

      const response = await adminService.assignPlanToUser({
        user_id: selectedUser.id,
        plan_id: assignForm.plan_id,
        billing_cycle: assignForm.billing_cycle,
        is_free: assignForm.is_free,
        custom_price: assignForm.custom_price ? parseFloat(assignForm.custom_price) : undefined,
        expires_at: assignForm.expires_at || undefined,
        notes: assignForm.notes,
        custom_limits: Object.keys(customLimitsPayload).length > 0 ? customLimitsPayload : undefined
      } as any);

      if (response.success) {
        toast.success(t('admin.planAssigned'));
        setIsAssignPlanModalOpen(false);
        loadCompanies();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    if (!selectedUser || !paymentLinkForm.plan_id) return;

    setIsSaving(true);
    try {
      const response = await adminService.generatePaymentLink({
        user_id: selectedUser.id,
        plan_id: paymentLinkForm.plan_id,
        billing_cycle: paymentLinkForm.billing_cycle,
        currency: paymentLinkForm.currency,
        custom_price: paymentLinkForm.custom_price ? parseFloat(paymentLinkForm.custom_price) : undefined
      });

      if (response.success && response.data) {
        setGeneratedLink(response.data.payment_link);
        toast.success(t('admin.paymentLinkGenerated'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success(t('common.copied'));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openVideoCreditsModal = async (user: CompanyUser) => {
    setSelectedUser(user);
    setVideoCreditsBalance(null);
    setVideoCreditsTransactions([]);
    setVideoCreditsForm({ amount: '', notes: '' });
    setIsVideoCreditsModalOpen(true);
    setIsActionMenuOpen(null);
    setVideoCreditsLoading(true);
    try {
      const response = await adminService.getCompanyVideoCredits(user.id);
      if (response.success && response.data) {
        setVideoCreditsBalance(response.data.balance);
        setVideoCreditsTransactions(response.data.transactions || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setVideoCreditsLoading(false);
    }
  };

  const handleGrantVideoCredits = async () => {
    if (!selectedUser) return;
    const amount = parseInt(videoCreditsForm.amount, 10);
    if (!amount || amount <= 0) {
      toast.error(t('admin.videoCreditsInvalidAmount'));
      return;
    }
    setIsSaving(true);
    try {
      const response = await adminService.grantVideoCredits({
        user_id: selectedUser.id,
        amount,
        notes: videoCreditsForm.notes || undefined
      });
      if (response.success && response.data) {
        toast.success(t('admin.videoCreditsGranted', { count: amount }));
        setVideoCreditsBalance(response.data.balance);
        setVideoCreditsForm({ amount: '', notes: '' });
        // Reload transactions
        const refreshed = await adminService.getCompanyVideoCredits(selectedUser.id);
        if (refreshed.success && refreshed.data) {
          setVideoCreditsTransactions(refreshed.data.transactions || []);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const getSubscriptionBadge = (user: CompanyUser) => {
    const sub = user.subscriptions?.[0];
    if (!sub) {
      return <Badge variant="gray">{t('admin.noSubscription')}</Badge>;
    }
    if (sub.metadata?.is_free) {
      return <Badge variant="primary">{t('admin.freePlan')}</Badge>;
    }
    if (sub.metadata?.is_manual) {
      return <Badge variant="warning">{t('admin.manualPlan')}</Badge>;
    }
    return <Badge variant="success">{sub.plan?.name || t('admin.active')}</Badge>;
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('admin.companies')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('admin.companiesDesc')}</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-3">
            <Input
              wrapperClassName="flex-1"
              placeholder={t('admin.searchCompanies')}
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              leftIcon={<Search className="w-5 h-5" />}
            />
            <Select
              wrapperClassName="w-full md:w-48"
              options={[
                { value: '', label: t('admin.allPlans') },
                ...plans.map((plan) => ({ value: plan.id, label: plan.name }))
              ]}
              value={planFilter}
              onChange={(e) => { setPage(1); setPlanFilter(e.target.value); }}
            />
            {(search || planFilter) && (
              <Button variant="secondary" onClick={() => { setSearch(''); setPlanFilter(''); setPage(1); }} className="md:w-auto">
                {t('common.clear', 'Limpar')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card padding={false}>
        <div className="overflow-x-auto scrollbar-hide">
          {isLoading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 skeleton rounded-lg" />
              ))}
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4 dark:text-gray-600" />
              <p className="text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.company')}</th>
                  <th>{t('admin.email')}</th>
                  <th>{t('admin.currentPlan')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('admin.registeredAt')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium">
                            {company.company_name?.charAt(0).toUpperCase() || company.first_name?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {company.company_name || `${company.first_name} ${company.last_name}`}
                          </p>
                          {company.company_name && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{company.first_name} {company.last_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-gray-600 dark:text-gray-400">{company.email}</td>
                    <td>
                      {company.subscriptions?.[0]?.plan?.name || (
                        <span className="text-gray-400 dark:text-gray-500">{t('admin.noPlan')}</span>
                      )}
                    </td>
                    <td>{getSubscriptionBadge(company)}</td>
                    <td className="text-gray-500 dark:text-gray-400">
                      {new Date(company.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td>
                      <button
                        data-company-id={company.id}
                        onClick={() => setIsActionMenuOpen(isActionMenuOpen === company.id ? null : company.id)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-700">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              {t('common.previous')}
            </Button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              {t('common.next')}
            </Button>
          </div>
        )}
      </Card>

      {/* Action Menu - Rendered outside Card to avoid overflow issues */}
      {isActionMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsActionMenuOpen(null)}
          />
          <div
            className="fixed z-50 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1"
            style={{
              top: document.querySelector(`[data-company-id="${isActionMenuOpen}"]`)?.getBoundingClientRect().bottom ?? 0,
              left: Math.min(
                (document.querySelector(`[data-company-id="${isActionMenuOpen}"]`)?.getBoundingClientRect().right ?? 0) - 224,
                window.innerWidth - 240
              )
            }}
          >
            <button
              onClick={() => {
                const company = companies.find(c => c.id === isActionMenuOpen);
                if (company) openAssignPlanModal(company);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <CreditCard className="w-4 h-4" />
              {t('admin.assignPlan')}
            </button>
            <button
              onClick={() => {
                const company = companies.find(c => c.id === isActionMenuOpen);
                if (company) openPaymentLinkModal(company);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <LinkIcon className="w-4 h-4" />
              {t('admin.generatePaymentLink')}
            </button>
            <button
              onClick={() => {
                const company = companies.find(c => c.id === isActionMenuOpen);
                if (company) {
                  setAssignForm(prev => ({ ...prev, is_free: true }));
                  openAssignPlanModal(company);
                }
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Gift className="w-4 h-4" />
              {t('admin.giveFreePlan')}
            </button>
            {/* Créditos de vídeo viraram créditos de IA (04/11/2026): o item abaixo cobre os dois */}
            <button
              onClick={() => {
                const company = companies.find(c => c.id === isActionMenuOpen);
                if (company) {
                  setAiCreditsUser({ id: company.id, label: [company.company_name || `${company.first_name || ''} ${company.last_name || ''}`.trim(), company.email].filter(Boolean).join(' · ') });
                  setIsActionMenuOpen(null);
                }
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Sparkles className="w-4 h-4" />
              {t('admin.grantAiCredits')}
            </button>
          </div>
        </>
      )}

      {/* Assign Plan Modal */}
      <Modal
        isOpen={isAssignPlanModalOpen}
        onClose={() => setIsAssignPlanModalOpen(false)}
        title={t('admin.assignPlanTitle')}
        size="md"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedUser.company_name || `${selectedUser.first_name} ${selectedUser.last_name}`}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('admin.selectPlan')} *
              </label>
              <Select
                options={[
                  { value: '', label: t('admin.selectPlanPlaceholder') },
                  ...plans.map((plan) => ({ value: plan.id, label: plan.name }))
                ]}
                value={assignForm.plan_id}
                onChange={(e) => setAssignForm({ ...assignForm, plan_id: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.billingCycle')}
                </label>
                <Select
                  options={[
                    { value: 'monthly', label: t('subscription.billingCycle.monthly') },
                    { value: 'yearly', label: t('subscription.billingCycle.yearly') }
                  ]}
                  value={assignForm.billing_cycle}
                  onChange={(e) => setAssignForm({ ...assignForm, billing_cycle: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.expiresAt')}
                </label>
                <Input
                  type="date"
                  value={assignForm.expires_at}
                  onChange={(e) => setAssignForm({ ...assignForm, expires_at: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={assignForm.is_free}
                  onChange={(e) => setAssignForm({ ...assignForm, is_free: e.target.checked, custom_price: '' })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('admin.isFree')}</span>
              </label>
            </div>

            {!assignForm.is_free && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.customPrice')}
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t('admin.customPricePlaceholder')}
                  value={assignForm.custom_price}
                  onChange={(e) => setAssignForm({ ...assignForm, custom_price: e.target.value })}
                />
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Limites customizados (opcional)
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Sobrescreve os limites do plano. Deixe em branco para usar o limite padrão. Use -1 para ilimitado.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'campaigns', label: 'Campanhas' },
                  { key: 'projects', label: 'Projetos' },
                  { key: 'keywords', label: 'Keywords' },
                  { key: 'competitors', label: 'Concorrentes' },
                  { key: 'content_generations', label: 'Gerações de conteúdo' },
                  { key: 'team_members', label: 'Membros do time' },
                  { key: 'automated_workflows', label: 'Workflows' }
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</label>
                    <Input
                      type="number"
                      placeholder="padrão"
                      value={assignForm.custom_limits[key]}
                      onChange={(e) => setAssignForm({
                        ...assignForm,
                        custom_limits: { ...assignForm.custom_limits, [key]: e.target.value }
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('admin.notes')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={2}
                placeholder={t('admin.notesPlaceholder')}
                value={assignForm.notes}
                onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => setIsAssignPlanModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleAssignPlan}
                isLoading={isSaving}
                disabled={!assignForm.plan_id}
              >
                {t('admin.assignPlan')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Payment Link Modal */}
      <Modal
        isOpen={isPaymentLinkModalOpen}
        onClose={() => setIsPaymentLinkModalOpen(false)}
        title={t('admin.generatePaymentLinkTitle')}
        size="md"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedUser.company_name || `${selectedUser.first_name} ${selectedUser.last_name}`}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
            </div>

            {!generatedLink ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('admin.selectPlan')} *
                  </label>
                  <Select
                    options={[
                      { value: '', label: t('admin.selectPlanPlaceholder') },
                      ...plans.map((plan) => ({ value: plan.id, label: plan.name }))
                    ]}
                    value={paymentLinkForm.plan_id}
                    onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, plan_id: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.billingCycle')}
                    </label>
                    <Select
                      options={[
                        { value: 'monthly', label: t('subscription.billingCycle.monthly') },
                        { value: 'yearly', label: t('subscription.billingCycle.yearly') }
                      ]}
                      value={paymentLinkForm.billing_cycle}
                      onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, billing_cycle: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.currency', 'Currency')}
                    </label>
                    <Select
                      options={[
                        { value: 'brl', label: '🇧🇷 BRL (R$)' },
                        { value: 'usd', label: '🇺🇸 USD ($)' }
                      ]}
                      value={paymentLinkForm.currency}
                      onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, currency: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('admin.customPrice', 'Custom Price')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                      {paymentLinkForm.currency === 'usd' ? '$' : 'R$'}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={paymentLinkForm.custom_price}
                      onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, custom_price: e.target.value })}
                      className="pl-10"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('admin.customPriceHint', 'Leave as default or enter a custom price for this customer')}
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="secondary" onClick={() => setIsPaymentLinkModalOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleGeneratePaymentLink}
                    isLoading={isSaving}
                    disabled={!paymentLinkForm.plan_id}
                  >
                    {t('admin.generateLink')}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                    {t('admin.linkGenerated')}
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={generatedLink}
                      readOnly
                      className="flex-1 font-mono text-sm"
                    />
                    <Button variant="secondary" onClick={copyLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.sendLinkToCustomer')}
                </p>

                <div className="flex justify-end">
                  <Button variant="primary" onClick={() => setIsPaymentLinkModalOpen(false)}>
                    {t('common.close')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <AiCreditsModal
        isOpen={!!aiCreditsUser}
        onClose={() => setAiCreditsUser(null)}
        userId={aiCreditsUser?.id || null}
        userLabel={aiCreditsUser?.label}
      />

      {/* Video Credits Modal */}
      <Modal
        isOpen={isVideoCreditsModalOpen}
        onClose={() => setIsVideoCreditsModalOpen(false)}
        title={t('admin.videoCreditsTitle')}
        size="md"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedUser.company_name || `${selectedUser.first_name} ${selectedUser.last_name}`}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
            </div>

            {videoCreditsLoading ? (
              <div className="py-8 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : (
              <>
                {videoCreditsBalance && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                      <p className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">{t('admin.videoCreditsAvailable')}</p>
                      <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-200 tabular-nums">{videoCreditsBalance.available}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{t('admin.videoCreditsTotal')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{videoCreditsBalance.total}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{t('admin.videoCreditsUsed')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{videoCreditsBalance.used}</p>
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t('admin.grantVideoCreditsManual')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.videoCreditsAmount')} *</label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        placeholder={t('admin.videoCreditsAmountPlaceholder')}
                        value={videoCreditsForm.amount}
                        onChange={(e) => setVideoCreditsForm({ ...videoCreditsForm, amount: e.target.value })}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.videoCreditsNotes')}</label>
                      <Input
                        type="text"
                        placeholder={t('admin.videoCreditsNotesPlaceholder')}
                        value={videoCreditsForm.notes}
                        onChange={(e) => setVideoCreditsForm({ ...videoCreditsForm, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[10, 50, 100, 500].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setVideoCreditsForm(prev => ({ ...prev, amount: String(n) }))}
                        className="px-3 py-1 rounded-md text-xs font-semibold border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                      >
                        +{n}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="primary"
                      onClick={handleGrantVideoCredits}
                      isLoading={isSaving}
                      disabled={!videoCreditsForm.amount || parseInt(videoCreditsForm.amount, 10) <= 0}
                    >
                      <Plus className="w-4 h-4 mr-1" /> {t('admin.videoCreditsGrantBtn')}
                    </Button>
                  </div>
                </div>

                {videoCreditsTransactions.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('admin.videoCreditsHistory')}</p>
                    <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                      {videoCreditsTransactions.map(tx => {
                        const sign = tx.type === 'debit' ? '-' : '+';
                        const color = tx.type === 'debit'
                          ? 'text-rose-600 dark:text-rose-400'
                          : 'text-emerald-600 dark:text-emerald-400';
                        return (
                          <div key={tx.id} className="flex items-start justify-between gap-3 px-3 py-2 text-xs">
                            <div className="min-w-0 flex-1">
                              <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">{tx.type} · {tx.source}</p>
                              {tx.description && (
                                <p className="text-gray-700 dark:text-gray-300 truncate" title={tx.description}>{tx.description}</p>
                              )}
                              <p className="text-[10px] text-gray-400">{new Date(tx.created_at).toLocaleString('pt-BR')}</p>
                            </div>
                            <span className={`font-bold tabular-nums ${color}`}>{sign}{tx.amount}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <Button variant="secondary" onClick={() => setIsVideoCreditsModalOpen(false)}>
                    {t('common.close')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminCompaniesPage;
